import { createRoute } from '@tanstack/react-router';
import {
  DefinitionList,
  EmptyState,
  ErrorState,
  LoadingState,
  MetricCard,
  Mono,
  PageHeader,
  SectionCard,
  StatusBadge,
} from '@verydia/ui';
import { Activity, Building2, Layers, Package, Tags } from 'lucide-react';
import type { CapabilityState, PlatformInfoResponse } from '@verydia/contracts';
import { rootRoute } from './root.tsx';
import { apiBaseUrl, describeFailure, useHealth, usePlatformInfo } from '../lib/api.ts';
import { useConsoleContext } from '../lib/console-context.tsx';
import { useCatalogSummary, useOrganizationAudit } from '../lib/identity-queries.ts';
import { activityPresentation, isVisibleActivity } from '../lib/activity-presentation.ts';

/**
 * The affected resource, named rather than typed.
 *
 * Audit metadata records the display name at the time of the action, which is
 * what makes historical rows still readable after a rename. When it is absent
 * the cell stays empty — an id here would be noise, and the Audit Log has it.
 */
function resourceLabel(event: { metadata?: Record<string, unknown> | null }): string {
  const metadata = event.metadata ?? {};
  for (const key of ['display_name', 'display_name_after', 'name', 'slug', 'code']) {
    const value = metadata[key];
    if (typeof value === 'string' && value !== '') return value;
  }
  return '';
}

/** Enough to answer "what just happened here" without becoming the audit page. */
const RECENT_ACTIVITY_LIMIT = 6;

export const overviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: OverviewPage,
});

/**
 * Overview.
 *
 * The design rule §30 imposes, and the one that shaped this page: nothing here may
 * imply data we do not have. The four metric cards render an em dash, not a zero —
 * "0 products" and "we have not built products yet" are different statements, and
 * only one of them is true. Everything that IS real (API health, deployment
 * metadata, capability flags) is fetched live from the API and shown in full.
 */
/**
 * Overview.
 *
 * ORGANIZATION-SCOPED, ALWAYS.
 *
 * The context bar can say `N-Expert.ai / JurisMind / Production` while this page
 * is open, and these numbers still describe N-Expert.ai as a whole. A dashboard
 * that quietly filtered itself by the selected product would be the worst kind of
 * wrong: every number plausible, none of them answering the question the heading
 * asks. Product-specific counts already live on product detail.
 *
 * WHAT IS ABSENT, AND WHY
 *
 * There are no customer or licence cards. Not zero — absent. A zero says "this
 * exists and none have been created", and for resource types that have not
 * shipped that is simply false. What the platform can and cannot do is answered
 * once, by the capability manifest below, from the API.
 */
function OverviewPage() {
  const context = useConsoleContext();
  const organizationId = context.organization?.id ?? null;
  const summary = useCatalogSummary(organizationId);
  const audit = useOrganizationAudit(organizationId);

  const totals = summary.data?.data ?? null;
  const recent = (audit.data?.data.data ?? [])
    .filter((event) => isVisibleActivity(event.action))
    .slice(0, RECENT_ACTIVITY_LIMIT);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={context.organization ? `${context.organization.name} overview` : 'Overview'}
        description="Catalog totals for this organization, and what this deployment can currently do."
      />

      {/*
        Three cards, all labelled "Active".

        Naming the lifecycle in the label is not pedantry: a count called
        "Products" that silently excludes archived ones is a number nobody can
        reconcile against the list page.
      */}
      <section aria-label="Catalog summary" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Active products"
          value={totals?.products.active ?? null}
          icon={<Package />}
          hint={
            summary.isPending
              ? 'Loading…'
              : totals === null
                ? 'Unavailable'
                : totals.products.active === 0
                  ? 'Create a product to begin its licensing catalog.'
                  : 'Software licensed through Verydia Control.'
          }
        />
        <MetricCard
          label="Active environments"
          value={totals?.environments.active ?? null}
          icon={<Layers />}
          hint={
            summary.isPending
              ? 'Loading…'
              : totals === null
                ? 'Unavailable'
                : // From `classification`, never from a name that looks like one.
                  `${totals.environments.production} production · ${totals.environments.non_production} non-production`
          }
        />
        <MetricCard
          label="Active customers"
          value={totals?.customers.active ?? null}
          icon={<Building2 />}
          hint={
            summary.isPending
              ? 'Loading…'
              : totals === null
                ? 'Unavailable'
                : totals.customers.active === 0
                  ? 'Add the companies you license your software to.'
                  : 'Companies you license your software to.'
          }
        />
        <MetricCard
          label="Active licensed product plans"
          value={totals?.licensed_product_plans.active ?? null}
          icon={<Tags />}
          hint={
            summary.isPending
              ? 'Loading…'
              : totals === null
                ? 'Unavailable'
                : // Published and issuable are different questions (ADR-019).
                  `${totals.licensed_product_plans.issuance_ready} ready to issue licences`
          }
        />
      </section>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <PlatformStatusCard />
        <CapabilitiesCard />
      </div>

      <SectionCard
        title="Recent activity"
        description="Audit events for this organization, newest first."
        flush
      >
        {audit.isPending ? (
          <LoadingState label="Loading recent activity" />
        ) : audit.isError ? (
          <ErrorState
            description={describeFailure(audit.error).message}
            requestId={describeFailure(audit.error).requestId}
            onRetry={() => void audit.refetch()}
          />
        ) : recent.length === 0 ? (
          <EmptyState
            icon={<Activity />}
            title="No activity yet"
            description="Actions taken in the console and through the API are recorded here as they happen."
          />
        ) : (
          <ul className="divide-y divide-[var(--vc-border)]">
            {recent.map((event) => (
              <li key={event.id} className="flex items-baseline gap-4 px-5 py-3">
                <time
                  dateTime={event.occurred_at}
                  className="w-44 shrink-0 text-[12.5px] text-[var(--vc-text-subtle)] tabular-nums"
                >
                  {new Date(event.occurred_at).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </time>
                <span className="min-w-0 flex-1 text-[13px] text-[var(--vc-text)]">
                  {activityPresentation(event.action)?.label ?? null}
                </span>
                {/*
                  The resource's own name, when the audit record carries one.
                  The raw `resource_type` used to sit here — `licensed_product_plan`
                  is a column value, not something to put in front of an operator.
                */}
                <span className="shrink-0 text-[12px] text-[var(--vc-text-subtle)]">
                  {resourceLabel(event)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

function PlatformStatusCard() {
  const health = useHealth();
  const platform = usePlatformInfo();

  if (health.isPending || platform.isPending) {
    return (
      <SectionCard title="API health" description="Live status of the Verydia Control API." flush>
        <LoadingState label="Contacting the API" />
      </SectionCard>
    );
  }

  if (health.isError || platform.isError) {
    const failure = describeFailure(health.error ?? platform.error);
    return (
      <SectionCard title="API health" description="Live status of the Verydia Control API." flush>
        <ErrorState
          title={failure.kind === 'unreachable' ? 'API unreachable' : 'API returned an error'}
          description={failure.message}
          requestId={failure.requestId}
          onRetry={() => {
            void health.refetch();
            void platform.refetch();
          }}
        />
      </SectionCard>
    );
  }

  const info = platform.data.data;
  const service = health.data.data;

  return (
    <SectionCard
      title="API health"
      description="Live status of the Verydia Control API."
      actions={<StatusBadge tone="success">Operational</StatusBadge>}
    >
      <DefinitionList
        items={[
          { label: 'Endpoint', value: <Mono>{apiBaseUrl}</Mono> },
          { label: 'Service', value: <Mono>{service.service}</Mono> },
          // Deliberately NOT "Environment". The context bar already uses that word
          // for the vendor's environment (Development / Pilot / Production), and
          // this is the platform deployment it is talking to. Showing "Development"
          // and "prod" under the same label reads as a contradiction.
          { label: 'Deployment', value: info.environment },
          { label: 'Region', value: <Mono>{info.region}</Mono> },
          { label: 'Platform version', value: <Mono>{info.platform_version}</Mono> },
          { label: 'API version', value: <Mono>{info.api_version}</Mono> },
          { label: 'Instance uptime', value: formatUptime(service.uptime_seconds) },
          {
            label: 'Last request ID',
            value: <Mono>{health.data.requestId ?? 'not returned'}</Mono>,
          },
        ]}
      />
    </SectionCard>
  );
}

/**
 * How each capability reads, and what it must not be mistaken for.
 *
 * Keyed by the capability names the API publishes, and `satisfies` a complete
 * record of them — so a capability added to the manifest without a label, and
 * without somebody deciding what it promises, fails to compile rather than
 * appearing in production as a raw key.
 *
 * Nothing here decides whether a capability is live. That answer comes from the
 * server, every time. A hardcoded `LIVE` in the console is exactly how a page
 * ends up advertising something the deployment does not have.
 */
const CAPABILITY_PRESENTATION = {
  product_catalog: {
    title: 'Product catalog',
    description: 'Manage the software products this organization licenses.',
  },
  product_environments: {
    title: 'Product environments',
    description:
      'Define environments and classify each one as production or non-production. ' +
      'Classification is a record, not a permission.',
  },
  licensed_product_plans: {
    title: 'Licensed product plans',
    description:
      'Define commercial tiers. Application feature entitlements stay in your own software, ' +
      'and a published tier still needs licence terms before it can issue anything.',
  },
  management_api: {
    title: 'Management API',
    // "Live" alone reads as "issue me an API key". The plane exists and is
    // documented; machine credentials are a separate capability that does not.
    description: 'Everything the console does, callable and documented. Authenticated as a person.',
  },
  api_credentials: {
    title: 'API credentials',
    description: 'Machine-to-machine credentials for server-side integrations.',
  },
  customers: {
    title: 'Customers',
    description: 'Record the legal entities you sell to, independently of any one product.',
  },
  license_core: {
    title: 'Licence issuance',
    description: 'Issuing licences to a customer against a product, environment and plan.',
  },
  runtime_validation: {
    title: 'Runtime validation',
    description: 'Licence checks made by your software while it runs.',
  },
  webhooks: {
    title: 'Webhooks',
    description: 'Delivery of catalog and licence events to your endpoints.',
  },
  offline_licensing: {
    title: 'Offline licensing',
    description: 'Licence validation for software that cannot reach the network.',
  },
} satisfies Record<
  keyof PlatformInfoResponse['capabilities'],
  { title: string; description: string }
>;

/** Two states, said plainly. No dates, no milestone names, no "coming soon". */
const CAPABILITY_STATE: Record<CapabilityState, { label: string; tone: 'success' | 'neutral' }> = {
  LIVE: { label: 'Live', tone: 'success' },
  NOT_AVAILABLE: { label: 'Not available', tone: 'neutral' },
};

function CapabilitiesCard() {
  const platform = usePlatformInfo();

  return (
    <SectionCard
      title="Platform capabilities"
      description="What this deployment provides, reported by the API."
      flush
    >
      {platform.isPending ? (
        <LoadingState label="Reading capabilities" />
      ) : platform.isError ? (
        <ErrorState
          description={describeFailure(platform.error).message}
          requestId={describeFailure(platform.error).requestId}
          onRetry={() => void platform.refetch()}
        />
      ) : (
        <ul className="divide-y divide-[var(--vc-border-subtle)]">
          {Object.entries(platform.data.data.capabilities).map(([key, state]) => {
            const meta =
              CAPABILITY_PRESENTATION[key as keyof typeof CAPABILITY_PRESENTATION] ?? null;
            // A capability this build has no copy for is skipped rather than
            // rendered as a raw key. The compile-time `satisfies` above means
            // this only happens against a server newer than the console.
            const presentation = CAPABILITY_STATE[state as CapabilityState] ?? null;
            // Unknown name or unknown state: skip the row rather than render a
            // raw key, or crash. A console can be older than the API it talks to.
            if (meta === null || presentation === null) return null;

            return (
              <li key={key} className="flex items-start justify-between gap-4 px-4 py-2.5">
                <div className="min-w-0">
                  {/*
                    Name and status share one accessible label, so a screen
                    reader hears "Runtime validation, Not available" rather than
                    two unrelated fragments.
                  */}
                  <p className="text-[13px] text-[var(--vc-text)]">
                    <span className="sr-only">{`${meta.title}: ${presentation.label}. `}</span>
                    <span aria-hidden="true">{meta.title}</span>
                  </p>
                  <p className="mt-0.5 text-[11.5px] text-[var(--vc-text-subtle)]">
                    {meta.description}
                  </p>
                </div>
                <StatusBadge tone={presentation.tone}>
                  <span aria-hidden="true">{presentation.label}</span>
                </StatusBadge>
              </li>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
}

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}
